import { useEffect, useState, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { isBefore } from 'date-fns';
import AuthContext from './AuthContext';
import { getLoginRequest } from './config';
import { getEnvironmentVariable } from '../services/env';
import { userLoginSuccess } from '../features/UserSlice';

const AutoLogout = () => {
  const token = useSelector((state) => state.user.authentication.token);
  const dispatch = useDispatch();
  const msalInstance = useContext(AuthContext);
  const [inactivityTimer, setInactivityTimer] = useState(0);
  const [tokenExpiry, setTokenExpiry] = useState();
  const interval = useRef(setTimeout(() => {}, 0));

  /**
   * Inactivity time limit
   * @type {Number}
   */
  const sessionInactivityTimerLimit = getEnvironmentVariable(
    'IDLE_TIMEOUT_SECONDS'
  );

  /**
   * Events to track
   * @type {Array}
   */
  const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

  /**
   * Reset the inactivity timer
   */
  const resetInactivityTimer = () => {
    setInactivityTimer(0);
  };

  /**
   * Has the inactivity limit been reached?
   * @return {Bool}
   */
  const sessionInactivityTimerLimitReached = () => {
    return sessionInactivityTimerLimit <= inactivityTimer;
  };

  /**
   * Check if the auth token has expired
   * @return {Bool}
   */
  const hasTokenExpired = () => {
    try {
      if (tokenExpiry && isBefore(new Date(), tokenExpiry)) {
        return false;
      }
      return true;
    } catch (error) {
      return true;
    }
  };

  /**
   * Expire the session and logout
   */
  const expireSession = () => {
    // Clear inactivity timer
    resetInactivityTimer();
    // Logout
    msalInstance.logoutRedirect({
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: window.location.href.replace(
        window.location.search,
        ''
      ),
    });
  };

  /**
   * Silently renew the user token
   */
  const renewToken = async () => {
    try {
      const response = await msalInstance.acquireTokenSilent(getLoginRequest());
      const { account, idToken: token } = response;
      dispatch(userLoginSuccess({ account, token }));
      // Update the state timer
      setInactivityTimer(inactivityTimer + 1);
    } catch {
      expireSession();
    }
  };

  /**
   * Perform the tick to increment the inactity timer
   */
  const inactivityTick = () => {
    const activeAccount = msalInstance.getActiveAccount();
    // Has the inactivity timeout been reached?
    if (activeAccount && sessionInactivityTimerLimitReached()) {
      expireSession();
    } else if (hasTokenExpired()) {
      renewToken();
    } else {
      // Update the state timer
      setInactivityTimer(inactivityTimer + 1);
    }
  };

  useEffect(() => {
    try {
      const decodedToken = jwtDecode(token);
      setTokenExpiry(new Date(decodedToken.exp * 1000));
    } catch {
      setTokenExpiry(null);
    }
  }, [token]);

  useEffect(() => {
    // Add the window evetns to reset the timer
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });
    // Set interval to check token and inactivity
    interval.current = setTimeout(inactivityTick, 1000);
    return () => {
      clearTimeout(interval.current);
    };
  }, [inactivityTimer]);

  return null;
};

export default AutoLogout;
